import React, { useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { Card, CardBody } from "@/shared/components/Card";
import {
  FormContainer,
  FormGroupLabel,
} from "@/shared/components/form/FormElements";
import { useSelector } from "react-redux";
import styled from "styled-components";
import Select from "react-select";
import {
  CheckBoxInput,
  Label,
  FormItem,
  UnitSpan,
} from "../../../shared/components/form/FormElements";
import { colorLightGray, colorTextNew } from "../../../utils/palette";
import { useEffect } from "react";
import Error from "@/shared/components/form/Error";
import { useHistory, useLocation } from "react-router";
import FormField from "../../../shared/components/form/FormField";
import { postApi } from "../../../api/getUser";
import FormHeader from "../../../shared/components/form/FormHeader";
import { Desc } from "../../Reminders/MaintenanceReminders/MaintenanceRemindersForm";
import { NATEXT } from "../../../shared/constants";
import { addMsg } from "../../../features/snackbar/snackbarSlice";
import { useDispatch } from "react-redux";
import { fetchInspectionTasks } from "../../../features/inspectionTasks/inspectionTasksSlice";
import ErrorPopup from "../../../shared/components/ErrorPopup";
import { formStyles } from "../../../shared/constants/styles";
import { useAuth } from "../../../contexts/AuthContext";
import SubscribePopup from "../../../shared/components/SubscribePopup";

const InspectionTasksForm = ({ isHorizontal, isAboveError }) => {
  const { t } = useTranslation("common");
  const history = useHistory();
  const {
    handleSubmit,
    reset,
    register,
    control,
    watch,
    getValues,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm();

  const location = useLocation();
  const dispatch = useDispatch();

  const { tag } = useAuth();

  //State to show subscribe popup
  const [showSubscribePopup, setShowSubscribePopup] = useState(false);

  const rowData = location?.state?.rowData;
  const screen = location?.state?.screen;
  const type = location?.state?.type;

  const { userId } = useSelector((state) => state.user);
  const allInspectionTasks = useSelector((state) => state.inspectionTasks.list);

  //console.log(errors);

  //React hook form fields
  const inspectionTask = watch("inspectionTask");
  const passFail = watch("passFail");
  const info = watch("info");
  const photo = watch("photo");
  const instructions = watch("instructions");
  const passFailOption = watch("passFailOption");
  const photoOption = watch("photoOption");

  //To toggle error popup while adding/editing form
  const [showErrPopup, setShowErrPopup] = useState(false);

  //Check if any one of the attributes is checked
  const isAttributeChecked = passFail || info || photo;

  const attributesArr = [
    {
      id: 0,
      label: t("pass_fail"),
      value: "passFail",
      isChecked: passFail,
      desc: t("pass_fail_desc"),
    },
    {
      id: 1,
      label: t("info"),
      value: "info",
      isChecked: info,
      desc: t("info_desc"),
    },
    {
      id: 2,
      label: t("photo"),
      value: "photo",
      isChecked: photo,
      desc: t("photo_desc"),
    },
  ];

  const passFailOptions = [
    { label: t("mandatory"), value: 1 },
    { label: t("optional"), value: 0 },
  ];

  const allPhotoOptions = [
    { label: t("optional"), value: 0 },
    { label: t("mandatory"), value: 1 },
    { label: t("mandatory_on_fail"), value: 2 },
  ];

  //Using state for this since photoOptions will change depending on pass_fail check
  const [photoOptions, setPhotoOptions] = useState(allPhotoOptions.slice(0, 2));

  const [saveBtnLoading, setSaveBtnLoading] = useState(false);

  useEffect(() => {
    if (type === "edit") {
      console.log("data", rowData);
      setValue("inspectionTask", rowData.task_name);
      setValue(
        "instructions",
        (!rowData.description || rowData.description) === NATEXT
          ? ""
          : rowData.description
      );
      setValue("passFail", rowData.pass_required === 1 ? true : false);
      setValue("info", rowData.value_required === 1 ? true : false);
      setValue("photo", rowData.image_required === 1 ? true : false);
      setValue(
        "passFailOption",
        passFailOptions.find((o) => o.value === rowData.pass_mandatory)
      );
      setValue(
        "photoOption",
        allPhotoOptions.find((o) => o.value === rowData.image_mandatory)
      );

      if (+rowData.pass_required) {
        setPhotoOptions(allPhotoOptions);
      }
    } else {
      setValue("passFailOption", passFailOptions[0]);
      setValue("photoOption", allPhotoOptions[0]);
    }
  }, []);

  const handleCheckBoxChange = (onChange, e, attr) => {
    console.log(e);
    onChange(e);
    const { checked } = e.target;

    if (attr.value === "passFail") {
      if (checked) {
        setPhotoOptions(allPhotoOptions);
      } else {
        setPhotoOptions(allPhotoOptions.slice(0, 2));
        setValue("passFailOption", passFailOptions[0]);
        setValue("photoOption", allPhotoOptions[0]);
      }
    }

    if (attr.value === "photo") {
      if (!checked) {
        setValue("photoOption", allPhotoOptions[0]);
      }
    }
  };

  const handlePhotoOptionChange = (onChange, e) => {
    if (tag === "free-user" && e.value !== 0) {
      setShowSubscribePopup(true);
      return;
    }

    if (tag !== "free-user") {
      onChange(e);
    }
  };

  const onSubmit = async (data) => {
    try {
      if (type !== "edit") {
        //Duplicate task name check
        for (let task of allInspectionTasks) {
          if (
            task.task_name.toLocaleLowerCase() ===
            inspectionTask?.trim()?.toLocaleLowerCase()
          ) {
            setError(
              "inspectionTask",
              {
                type: "custom",
                message: t("dup_insp_task_name_error"),
              },
              { shouldFocus: true }
            );
            return;
          } else {
            clearErrors("inspectionTask");
          }
        }
      }
      setSaveBtnLoading(true);
      //const { inspectionTask, passFail, info, photo, instructions } = data;

      const inspectionTaskId =
        type === "edit" ? rowData.task_id : `${userId}_${Date.now()}`;

      const apiData = {
        querystring: "addEditInspectionTask",
        inspection_task_name: inspectionTask,
        pass_required: passFail ? 1 : 0,
        value_required: info ? 1 : 0,
        image_required: photo ? 1 : 0,
        pass_mandatory: passFailOption.value,
        image_mandatory: photoOption.value,
        description: instructions || "",
        operation: type === "edit" ? "update" : "create",
        inspection_task_id: inspectionTaskId,
        action: type === "edit" ? 2 : 1,
        source: "web",
        sync_version: "v2",
      };

      const snackbarMsg =
        type === "edit"
          ? t("record_edited_msg", { recordName: t("inspection_task") })
          : t("record_added_msg", { recordName: t("inspection_task") });

      console.log("apiData", apiData);
      const res = await postApi(apiData, "inspection");
      if (res.success) {
        const apiData = {
          //email: "testgroup@12.in",
          querystring: "get_all_inspection_tasks",
        };
        dispatch(fetchInspectionTasks(apiData));
        reset({
          inspectionTask: "",
          passFail: false,
          info: false,
          photo: false,
          instructions: "",
        });
        dispatch(addMsg(snackbarMsg));
        setSaveBtnLoading(false);
        history.push("/inspection/inspectiontasks", { fromCancel: false });
      } else {
        setSaveBtnLoading(false);
        setShowErrPopup(true);
        return;
      }
    } catch (e) {
      setSaveBtnLoading(false);
      setShowErrPopup(true);
      return;
    }
  };

  const handleCancel = () => {
    reset({
      inspectionTask: "",
      passFail: false,
      info: false,
      photo: false,
      instructions: "",
    });

    history.push("/inspection/inspectiontasks", { fromCancel: true });
  };

  const inputStyles = {
    //borderColor: colorLightGray,
    color: colorTextNew,
    border: "none",
    borderRadius: 5,
    fontSize: "14px",
    //width: '200px'
  };

  return (
    <>
      {showErrPopup && (
        <ErrorPopup showPopup={showErrPopup} setShowPopup={setShowErrPopup} />
      )}
      {showSubscribePopup && (
        <SubscribePopup
          showPopup={showSubscribePopup}
          setShowPopup={setShowSubscribePopup}
          message={t("subscribe_msg")}
        />
      )}
      <FormContainer
        horizontal={false}
        onSubmit={handleSubmit(onSubmit)}
        //style={{ width: "100%" }}
      >
        <FormHeader
          title={
            type === "edit"
              ? t("edit_inspection_task")
              : t("add_inspection_task")
          }
          isSaveLoading={saveBtnLoading}
          handleCancel={handleCancel}
        />

        <Container>
          <Row>
            <Col>
              <Card>
                <CardBody formcard="true">
                  <Row className="pb-2">
                    <Col md={6}>
                      <FormGroupLabel>
                        {t("inspection_task")}
                        {/* <Tooltip text="Set a Reminder once in every...">
                          <Info />
                        </Tooltip> */}
                      </FormGroupLabel>
                      <FormItem>
                        <FormField
                          name="inspectionTask"
                          control={control}
                          component="input"
                          //type="number"
                          //errors={errors}
                          rules={{
                            required: t("required_err"),
                          }}
                          defaultValue=""
                          isAboveError={isAboveError}
                          placeholder={t("inpection_task_name")}
                          style={inputStyles}
                        />
                      </FormItem>
                      {errors?.inspectionTask && (
                        <Error error={errors.inspectionTask.message} />
                      )}
                    </Col>
                  </Row>
                  <Row className="py-2">
                    <Col md={10}>
                      <FormGroupLabel style={{ marginBottom: "0px" }}>
                        {t("task_actions_label")}
                        {/* <Tooltip text="Set a Reminder once in every...">
                          <Info />
                        </Tooltip> */}
                      </FormGroupLabel>

                      {attributesArr.map((attr) => {
                        return (
                          <Row key={attr.id}>
                            <AttrContainer>
                              <Controller
                                key={attr.id}
                                name={attr.value}
                                control={control}
                                rules={{
                                  required: !isAttributeChecked
                                    ? t("one_action_reqd_err_msg")
                                    : false,
                                }}
                                errors={errors}
                                render={({
                                  field: { onChange, value, ref },
                                }) => (
                                  <div>
                                    <CheckBoxInput
                                      name={attr.value}
                                      type="checkbox"
                                      onChange={(e) =>
                                        handleCheckBoxChange(onChange, e, attr)
                                      }
                                      style={{
                                        width: "auto",
                                        margin: "0 4px 0 0",
                                      }}
                                      id={attr.value}
                                      checked={attr.isChecked}
                                    />
                                    <Label htmlFor={attr.value}>
                                      {attr.label}
                                    </Label>
                                  </div>
                                )}
                              />
                              <Desc>{attr.desc}</Desc>

                              {attr.value === "passFail" && (
                                <div
                                  style={{ maxWidth: "240px" }}
                                  className="my-2"
                                >
                                  <Controller
                                    name="passFailOption"
                                    control={control}
                                    //errors={errors}
                                    render={({
                                      field: { onChange, value, ref },
                                    }) => (
                                      <Select
                                        options={passFailOptions}
                                        //defaultValue={passFailOptions[0]}
                                        value={value}
                                        onChange={onChange}
                                        //onCreateOption={handleCreate}
                                        placeholder={t("select")}
                                        styles={formStyles}
                                        isDisabled={!passFail}
                                      />
                                    )}
                                    rules={{ required: t("required_err") }}
                                  />

                                  {errors?.passFailOption && (
                                    <Error
                                      error={errors.passFailOption.message}
                                    />
                                  )}
                                </div>
                              )}

                              {attr.value === "photo" && (
                                <div
                                  style={{
                                    width: "240px",
                                  }}
                                  className="my-2"
                                >
                                  <Controller
                                    name="photoOption"
                                    control={control}
                                    //errors={errors}
                                    render={({
                                      field: { onChange, value, ref },
                                    }) => (
                                      <Select
                                        options={photoOptions}
                                        //defaultValue={photoOptions[0]}
                                        value={value}
                                        onChange={(e) =>
                                          handlePhotoOptionChange(onChange, e)
                                        }
                                        //onCreateOption={handleCreate}
                                        placeholder={t("select")}
                                        styles={formStyles}
                                        isDisabled={!photo}
                                      />
                                    )}
                                    rules={{ required: t("required_err") }}
                                  />

                                  {errors?.photoOption && (
                                    <Error error={errors.photoOption.message} />
                                  )}
                                </div>
                              )}
                            </AttrContainer>
                          </Row>
                        );
                      })}
                      {errors?.photo && <Error error={errors.photo.message} />}
                    </Col>
                  </Row>
                  <Row className="py-2">
                    <Col md={10}>
                      <FormGroupLabel>
                        {t("instructions_to_driver_label")}
                        {/* <Tooltip text="Set a Reminder once in every...">
                          <Info />
                        </Tooltip> */}
                      </FormGroupLabel>
                      {/* <Controller
                        name="instructions"
                        control={control}
                        errors={errors}
                        render={({ field: { onChange, value, ref } }) => (
                          <Textarea
                            rows={2}
                            id="instructions"
                            name="instructions"
                            onChange={onChange}
                          ></Textarea>
                        )}
                      /> */}

                      <FormItem>
                        <FormField
                          name="instructions"
                          control={control}
                          component="input"
                          //type="number"
                          //errors={errors}
                          // rules={{
                          //   required: "Inspection Task Name is required",
                          // }}
                          defaultValue=""
                          isAboveError={isAboveError}
                          placeholder={t("instructions_to_driver_placeholder")}
                          style={inputStyles}
                        />
                      </FormItem>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </FormContainer>
    </>
  );
};

InspectionTasksForm.propTypes = {
  isHorizontal: PropTypes.bool,
  isAboveError: PropTypes.bool,
};

InspectionTasksForm.defaultProps = {
  isHorizontal: false,
  isAboveError: false,
};

export default InspectionTasksForm;

export const Textarea = styled.textarea``;

export const AttrContainer = styled(Col)`
  display: flex;
  margin: 4px 12px;
  //border: 1px solid ${colorLightGray};
  flex-direction: column;
  box-shadow: 1px 1px 3px ${colorLightGray};
  padding: 8px;
  // p {
  //   margin: 0 0 4px 8px;
  // }
`;
